import {
  useCreateAndStartTaskMutation,
  useCreateTaskMutation,
} from '@/queries/tasks';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import FileOutlined from '@ant-design/icons/FileOutlined';
import FormOutlined from '@ant-design/icons/FormOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import Button from 'antd/es/button';
import Form from 'antd/es/form';
import Spin from 'antd/es/spin';
import Steps from 'antd/es/steps';
import Handlebars from 'handlebars';
import { useEffect, useMemo, useState } from 'react';

import type { TaskTemplate } from '@mai/types';

import TemplateFieldsStep from './TemplateFieldsStep';
import TemplatePreviewStep from './TemplatePreviewStep';
import TemplateSelectionStep from './TemplateSelectionStep';
import { DEFAULT_DESCRIPTION, DEFAULT_TITLE } from './constants';
import {
  generateTaskFromTemplate,
  registerHandlebarsHelpers,
} from './handlebarsHelpers';
import {
  FieldsScrollContainer,
  LoadingContainer,
  StepActions,
  StepContent,
  StepsContainer,
  StyledFormContainer,
} from './styles';
import { type CreateTaskFormProps, type TemplateFormData } from './types';

import { logger } from '@utils/logger';

// Register Handlebars helpers
registerHandlebarsHelpers();

const CreateTaskForm = ({ afterSubmit, projectId }: CreateTaskFormProps) => {
  const [templateForm] = Form.useForm<TemplateFormData>();
  const [currentStep, setCurrentStep] = useState(0);
  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<TaskTemplate | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  // Task mutation hooks
  const createTaskMutation = useCreateTaskMutation();
  const createAndStartTaskMutation = useCreateAndStartTaskMutation();

  // Watch values for the template form
  const rawTemplateFieldValues = Form.useWatch('fieldValues', templateForm);
  const templateFieldValues = useMemo(() => {
    return rawTemplateFieldValues ?? {};
  }, [rawTemplateFieldValues]);
  const customTitle = Form.useWatch(['fieldValues', 'title'], templateForm);
  const customDescription = Form.useWatch(
    ['fieldValues', 'description'],
    templateForm,
  );

  // Live preview of template with current values
  const templatePreview = useMemo(() => {
    if (!selectedTemplate) {
      // For custom tasks (no template), just show title and description
      if (customTitle && customDescription) {
        return `# ${customTitle}\n\n${customDescription}`;
      }
      return '';
    }

    try {
      const compiledTemplate = Handlebars.compile(selectedTemplate.template);
      const compiledTitleTemplate = Handlebars.compile(
        selectedTemplate.titleTemplate,
      );
      const title = compiledTitleTemplate(templateFieldValues);
      const content = compiledTemplate(templateFieldValues);
      return `# ${title}\n\n${content}`;
    } catch (error) {
      return `Error previewing template: ${error instanceof Error ? error.message : String(error)}`;
    }
  }, [selectedTemplate, templateFieldValues, customTitle, customDescription]);

  // Fetch template data
  const fetchTemplates = () => {
    setLoading(true);
    // This would be replaced with an actual API call
    setTimeout(() => {
      setTemplates([
        {
          taskTemplateId: 'template-001',
          projectId: null,
          title: 'Market Analysis',
          titleTemplate: 'Market Analysis - {{productName}}',
          description:
            'Template for creating a market analysis task for a healthcare product',
          template: `## Task Overview
Conduct a comprehensive market analysis for {{productName}}.

## Task Objectives
1. Assess current and projected market size with 5-year forecast
2. Analyze competitive landscape, positioning, and differentiation factors
3. Evaluate pricing strategy, reimbursement potential, and market access barriers
4. Identify key regulatory requirements, commercial risks, and opportunities
5. Develop strategic recommendations with implementation roadmap

## Required Analysis Areas
- Current market size and growth projections
- Patient population analysis and patient journey mapping
- Competitive landscape assessment and product benchmarking
- Pricing strategy and reimbursement environment
- Regional market prioritization and market access strategies
- Regulatory pathway and approval requirements
- Key commercial risks and opportunities
- Manufacturing and supply chain considerations
- Strategic recommendations with implementation timelines

## Deliverable Format
Complete market analysis report with executive summary, detailed findings, and actionable recommendations. Report should include competitive positioning matrix, pricing strategy options, regulatory pathway assessment, and implementation roadmap with key milestones.
      `,
          fields: [
            {
              name: 'productName',
              title: 'Product Name',
              description: 'Name of the product to analyze',
              type: 'text',
              required: true,
              default: '',
              example: 'Cardiomet-XR',
            },
          ],
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          taskTemplateId: 'template-002',
          projectId: null,
          title: 'Due Diligence',
          titleTemplate: 'Due Diligence: {{companyName}}',
          description:
            'Template for creating a comprehensive due diligence task for a biotech company',
          template: `## Task Overview
Conduct comprehensive due diligence assessment of {{companyName}}.

## Task Objectives
1. Evaluate scientific validity, technical feasibility, and market potential of core technology
2. Assess intellectual property portfolio strength, protection strategy, and freedom to operate
3. Review product pipeline, development timelines, and clinical/preclinical data quality
4. Analyze financial health, capital structure, funding needs, and valuation metrics
5. Evaluate leadership team capabilities, organizational structure, and key personnel dependencies
6. Identify key technical, commercial, and regulatory risks with mitigation strategies

## Required Analysis Areas
- Scientific & technical validation with critical review of underlying data
- Intellectual property assessment including freedom to operate analysis
- Market opportunity analysis with competitive positioning
- Product pipeline evaluation and clinical data assessment
- Regulatory pathway assessment and approval timelines
- Commercial strategy review and go-to-market approach
- Manufacturing and CMC (Chemistry, Manufacturing, Controls) considerations
- Financial analysis including capital structure and funding requirements
- Management team evaluation and organizational capabilities assessment
- Risk assessment with mitigation strategies

## Deliverable Format
Comprehensive due diligence report with executive summary, detailed findings, and investment recommendation. Report should include scientific assessment, IP landscape analysis, competitive positioning, financial projections, risk mitigation strategies, and a clear go/no-go recommendation with supporting rationale.
      `,
          fields: [
            {
              name: 'companyName',
              title: 'Company Name',
              description: 'Name of the biotech company to evaluate',
              type: 'text',
              required: true,
              default: '',
              example: 'NeuraTech Therapeutics',
            },
          ],
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        {
          taskTemplateId: 'template-003',
          projectId: null,
          title: 'Investment Portfolio Analysis',
          titleTemplate: 'Investment Portfolio Analysis for {{targetInvestor}}',
          description:
            'Template for creating a comprehensive investment portfolio analysis task',
          template: `## Task Overview
Analyze the current investment portfolio holdings for {{targetInvestor}}.
{{#if targetAreas}}
Target Areas of Interest
{{targetAreas}}
{{/if}}
## Task Objectives

1. Assess performance of existing portfolio holdings across all sectors in {{targetInvestor}}'s current investments
2. Analyze current investment allocation, diversification levels, and sector concentration within the existing portfolio
3. Evaluate actual risk exposure, realized risk-adjusted returns, and existing downside protection measures
4. Identify optimization opportunities based on {{targetInvestor}}'s investment criteria compared to current portfolio composition
5. Provide strategic recommendations for portfolio adjustment, including specific entry/exit positions and optimal timing

## Required Analysis Areas

- Current portfolio performance evaluation (absolute and relative) with competitive benchmarking
- Existing asset allocation analysis and diversification assessment of held investments
- Risk metrics assessment of current holdings (volatility, drawdowns, Sharpe ratio, correlation analysis)
- Alignment between existing investments and {{targetInvestor}}'s investment criteria and focus areas
- Current fee and expense structures impacting net returns of portfolio
- Tax efficiency review of existing holdings and optimization opportunities
- ESG/Impact profile of current investments and compliance assessment
- Liquidity analysis of existing positions and exit strategy evaluation
- Interdependencies and synergy between currently held portfolio companies
- Required follow-on investment commitments for existing portfolio companies
- Rebalancing recommendations with specific implementation plan for the current portfolio

## Deliverable Format
Comprehensive analysis report of existing portfolio with executive summary, detailed findings on current holdings, and actionable recommendations. Report should include benchmark comparison of actual performance, risk-adjusted performance metrics of current investments, allocation optimization models based on existing holdings, alignment assessment between current portfolio and investor criteria, and an implementation plan for recommended adjustments to the investment strategy.`,
          fields: [
            {
              name: 'targetInvestor',
              title: 'Target Investor/VC',
              description:
                'Name of the investor or VC firm this analysis is for',
              type: 'text',
              required: true,
              default: '',
              example: 'HealthTech Ventures',
            },
            {
              name: 'targetAreas',
              title: 'Target Areas',
              description: 'Areas of investment this analysis is for',
              type: 'text',
              required: false,
              default: '',
              example: 'HealthTech, Biotech, Pharma',
            },
          ],
          createdAt: new Date(),
          updatedAt: new Date(),
        },
      ]);
      setLoading(false);
    }, 300);
  };

  // Initialize form and fetch templates when component mounts
  useEffect(() => {
    fetchTemplates();
    // Initialize form with basic fields for custom tasks
    templateForm.setFieldsValue({
      templateId: undefined,
      fieldValues: {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
      },
    });
  }, [templateForm]);

  // Handle next button click
  const next = async () => {
    // Validate forms before advancing to ensure all requirements are met
    try {
      if (currentStep === 1) {
        // Validate template form fields
        await templateForm.validateFields();
      }

      // If we get here, validation passed or wasn't needed for this step
      setCurrentStep(currentStep + 1);
    } catch (error) {
      // Validation failed, stay on current step
      // No need to log error as the form UI will show validation messages
    }
  };

  // Handle previous button click
  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleTemplateSelect = (templateId: string | null) => {
    if (!templateId) {
      // User chose to create a basic task
      setSelectedTemplate(null);

      // Initialize form with just title and description
      templateForm.setFieldsValue({
        templateId: undefined,
        fieldValues: {
          title: DEFAULT_TITLE,
          description: DEFAULT_DESCRIPTION,
        },
      });
      return;
    }

    const template = templates.find(
      (t) => t.taskTemplateId === templateId,
    ) as TaskTemplate;
    setSelectedTemplate(template);

    // Initialize the template form with default values and set up required fields for validation
    const initialValues: TemplateFormData = {
      templateId: template.taskTemplateId,
      fieldValues: {},
    };

    // Initialize field values based on defaults or set empty strings for required fields
    // so that validation gets triggered immediately for required fields
    template.fields.forEach((field) => {
      // Check if the field has a default value
      if (field.default !== null && field.default !== undefined) {
        initialValues.fieldValues[field.name] = field.default;
      }
      // For required fields without defaults, initialize with empty string
      // to ensure validation happens immediately
      else if (field.required) {
        initialValues.fieldValues[field.name] = '';
      }
    });

    // Set the form values with the initialValues
    templateForm.setFieldsValue(initialValues);
  };

  // Create a task from the template form
  const createTemplateTask = async (startTask = false): Promise<void> => {
    try {
      // Validate the form before proceeding
      const values = await templateForm.validateFields();

      if (selectedTemplate) {
        // Create a task from the selected template
        const task = generateTaskFromTemplate(
          selectedTemplate,
          values.fieldValues,
        );

        // Ensure task has all required fields for API
        const taskData = {
          projectId, // Use the projectId passed as prop
          title: task.title,
          description: task.description,
          metadata: {}, // Default empty metadata object
        };

        if (startTask) {
          await createAndStartTaskMutation.mutateAsync({
            ...taskData,
            shouldStart: true,
          });
        } else {
          await createTaskMutation.mutateAsync(taskData);
        }

        afterSubmit([task]);
      } else {
        // Create a basic task with just title and description
        const { fieldValues } = values;
        const taskData = {
          projectId, // Use the projectId passed as prop
          title: fieldValues.title as string,
          description: fieldValues.description as string,
          metadata: {}, // Empty metadata object
        };

        if (startTask) {
          await createAndStartTaskMutation.mutateAsync({
            ...taskData,
            shouldStart: true,
          });
        } else {
          await createTaskMutation.mutateAsync(taskData);
        }

        afterSubmit([taskData]);
      }

      templateForm.resetFields();
      resetForm();
    } catch (error) {
      // Validation errors are handled by the form
      logger.error({}, 'Error creating task', { error });
    }
  };

  const resetForm = () => {
    setCurrentStep(0);
    setSelectedTemplate(null);
    templateForm.setFieldsValue({
      templateId: undefined,
      fieldValues: {
        title: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
      },
    });
  };

  // Define steps with content for each
  const steps = [
    {
      title: 'Select Template',
      icon: <FileOutlined />,
      content: loading ? (
        <LoadingContainer>
          <Spin tip="Loading templates..." />
        </LoadingContainer>
      ) : (
        <TemplateSelectionStep
          templates={templates}
          selectedTemplate={selectedTemplate}
          onTemplateSelect={handleTemplateSelect}
          allowNoTemplate={true}
        />
      ),
    },
    {
      title: 'Fill Details',
      icon: <FormOutlined />,
      content: (
        <FieldsScrollContainer>
          <Form
            form={templateForm}
            layout="vertical"
            name="templateForm"
            initialValues={{
              templateId: selectedTemplate?.taskTemplateId,
              fieldValues: {},
            }}
          >
            <TemplateFieldsStep selectedTemplate={selectedTemplate} />
          </Form>
        </FieldsScrollContainer>
      ),
    },
    {
      title: 'Preview & Confirm',
      icon: <EyeOutlined />,
      content: (
        <TemplatePreviewStep
          renderedPreview={templatePreview}
          form={templateForm}
          onCreateDraft={() => createTemplateTask(false)}
          onCreateAndStart={() => createTemplateTask(true)}
          hideButtons={true}
        />
      ),
    },
  ];

  // Determine if the next button should be disabled
  const isNextDisabled = () => {
    if (currentStep === 1) {
      if (selectedTemplate) {
        // For template-based tasks, check that all required fields have values
        return selectedTemplate.fields
          .filter((field) => field.required)
          .some((field) => {
            const value = templateFieldValues[field.name];
            return value === undefined || value === null || value === '';
          });
      } else {
        // For custom tasks, check that title and description are provided
        return !customTitle || !customDescription;
      }
    }

    return false;
  };

  return (
    <StyledFormContainer>
      <StepsContainer>
        <Steps
          current={currentStep}
          items={steps.map((item, index) => ({
            title: item.title,
            status:
              index < currentStep
                ? 'finish'
                : index === currentStep
                  ? 'process'
                  : 'wait',
            icon: item.icon,
          }))}
          size="small"
        />
      </StepsContainer>

      <StepContent>
        {/* Step 0 - Template Selection */}
        <div
          style={{
            display: currentStep === 0 ? 'block' : 'none',
            height: '100%',
          }}
        >
          {loading ? (
            <LoadingContainer>
              <Spin tip="Loading templates..." />
            </LoadingContainer>
          ) : (
            <TemplateSelectionStep
              templates={templates}
              selectedTemplate={selectedTemplate}
              onTemplateSelect={handleTemplateSelect}
              allowNoTemplate={true}
            />
          )}
        </div>

        {/* Step 1 - Fill Details */}
        <div
          style={{
            display: currentStep === 1 ? 'block' : 'none',
            height: '100%',
          }}
        >
          <FieldsScrollContainer>
            <Form
              layout="vertical"
              form={templateForm}
              name="templateForm"
              initialValues={{
                templateId: selectedTemplate?.taskTemplateId,
                fieldValues: {},
              }}
            >
              <TemplateFieldsStep selectedTemplate={selectedTemplate} />
            </Form>
          </FieldsScrollContainer>
        </div>

        {/* Step 2 - Preview & Confirm */}
        <div
          style={{
            display: currentStep === 2 ? 'block' : 'none',
            height: '100%',
          }}
        >
          <TemplatePreviewStep
            renderedPreview={templatePreview}
            form={templateForm}
            onCreateDraft={() => createTemplateTask(false)}
            onCreateAndStart={() => createTemplateTask(true)}
            hideButtons={true}
          />
        </div>
      </StepContent>

      <StepActions>
        {currentStep > 0 && (
          <div style={{ marginRight: 'auto' }}>
            <Button
              size="large"
              onClick={prev}
              htmlType="button"
              disabled={currentStep === 0}
            >
              Previous
            </Button>
          </div>
        )}

        {currentStep < steps.length - 1 && (
          <Button
            type="primary"
            size="large"
            onClick={next}
            disabled={isNextDisabled()}
            htmlType="button"
          >
            Next
          </Button>
        )}

        {currentStep === steps.length - 1 && (
          <div style={{ display: 'flex', gap: '12px' }}>
            <Button
              icon={<SaveOutlined />}
              size="large"
              onClick={() => createTemplateTask(false)}
              htmlType="button"
              loading={createTaskMutation.isPending}
              disabled={
                createTaskMutation.isPending ||
                createAndStartTaskMutation.isPending
              }
            >
              Create Draft
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<PlayCircleOutlined />}
              onClick={() => createTemplateTask(true)}
              htmlType="button"
              loading={createAndStartTaskMutation.isPending}
              disabled={
                createTaskMutation.isPending ||
                createAndStartTaskMutation.isPending
              }
            >
              Create and Start
            </Button>
          </div>
        )}
      </StepActions>
    </StyledFormContainer>
  );
};

export default CreateTaskForm;
